import Bold from '@tiptap/extension-bold';
import Document from '@tiptap/extension-document';
import HardBreak from '@tiptap/extension-hard-break';
import Link from '@tiptap/extension-link';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import { EditorContent, useEditor, JSONContent } from '@tiptap/react';
import linkify from 'linkify-it';
import React, { useEffect, useMemo, memo } from 'react';

import { Box, Text as BokehText, Tooltip } from '@vimeo/bokeh';

import { T } from '../../translations';
import { convertDateTime } from '../../utils';
import MentionExtension from '../Mention/MentionExtension';
import SearchHighlightExtension from '../SearchComments/SearchHighlightExtension';

// Same configuration as @vimeo/ui/core/Linkify for consistent URL detection
const linkifyObj = new linkify(
  {
    'ftp:': { validate: () => false },
    'mailto:': { validate: () => false },
  },
  { fuzzyIP: false, fuzzyEmail: false },
);

/**
 * Traverses TipTap JSON content and adds link marks to text nodes
 * that contain URLs but don't already have a link mark.
 * This is needed because autolink only works during editing,
 * not for existing content in a read-only editor.
 *
 * Uses linkify-it (same library as @vimeo/ui/core/Linkify) to ensure
 * consistent URL detection between the timeline and the side panel.
 */
const addLinkMarksToContent = (content: JSONContent): JSONContent | JSONContent[] => {
  if (!content) return content;

  if (Array.isArray(content)) {
    return content.flatMap(addLinkMarksToContent);
  }

  if (content.type === 'text' && content.text) {
    const hasLinkMark = content.marks?.some((mark) => mark.type === 'link');
    if (hasLinkMark) return content;

    const matches = linkifyObj.match(content.text);
    if (!matches || matches.length === 0) return content;

    const parts: JSONContent[] = [];
    let lastIndex = 0;

    for (const match of matches) {
      if (match.index > lastIndex) {
        parts.push({
          ...content,
          text: content.text.slice(lastIndex, match.index),
        });
      }
      parts.push({
        ...content,
        text: match.raw,
        marks: [
          ...(content.marks || []),
          {
            type: 'link',
            attrs: { href: match.url, target: '_blank', rel: 'noopener noreferrer' },
          },
        ],
      });
      lastIndex = match.lastIndex;
    }

    if (lastIndex < content.text.length) {
      parts.push({
        ...content,
        text: content.text.slice(lastIndex),
      });
    }

    return parts;
  }

  if (content.content) {
    return {
      ...content,
      content: content.content.flatMap(addLinkMarksToContent),
    };
  }

  return content;
};

type Props = {
  text?: string;
  editedTime?: string;
  isResolved?: boolean;
  searchQuery?: string;
  enableLinks?: boolean;
};

export const RichCommentRender = ({
  text = '',
  editedTime,
  isResolved,
  searchQuery,
  enableLinks,
}: Props) => {
  const richContent = useMemo(() => {
    try {
      const parsed = JSON.parse(text);
      if (enableLinks) {
        return addLinkMarksToContent(parsed);
      }
      return parsed;
    } catch (_e) {
      return;
    }
  }, [text, enableLinks]);

  const editor = useEditor({
    editable: false,
    extensions: [
      Document,
      Text,
      Paragraph,
      Bold,
      HardBreak,
      ...(enableLinks
        ? [
            Link.configure({
              openOnClick: true,
              autolink: true,
              HTMLAttributes: {
                target: '_blank',
                rel: 'noopener noreferrer',
              },
            }),
          ]
        : []),
      SearchHighlightExtension.configure({
        searchHighlightClass: 'highlighted-text',
      }),
      MentionExtension,
    ],
    content: richContent,
  });

  useEffect(() => {
    if (richContent) {
      setTimeout(() => {
        editor?.chain().setContent(richContent).run();
      }, 0);
    }
  }, [richContent, editor]);

  useEffect(() => {
    if (editor) {
      editor.commands.setSearchTerm(searchQuery || '');
    }
  }, [searchQuery, editor]);

  useEffect(() => {
    if (editor) {
      const mentionedResults = editor.storage.searchHighlight?.mentionResults ?? [];
      const transaction = editor.state.tr;
      mentionedResults.forEach((result: { from: number; to: number; pos: number }) => {
        const { from, to, pos } = result;
        const currentNode = transaction.doc.nodeAt(pos);
        transaction.setNodeMarkup(pos, null, { ...currentNode?.attrs, highlight: { from, to } });
      });
      editor.view.dispatch(transaction.setTime(Date.now()));
    }
    // The hook needs "editor" for ref but we don't want this to run everytime it changes
    // We only update mention node highlight here and that should run when the mentioned
    // nodes in the search results are updated.
    // eslint-disable-next-line
  }, [editor?.storage?.searchHighlight?.mentionResults?.length]);

  const defaultRichTextViewerStyles = useMemo(
    () => ({
      '.rich-comment-viewer': {
        display: 'inline',
        fontSize: 'text-sm',
        color: 'text-primary',
      },
      '.ProseMirror': {
        paddingLeft: 0,
        display: 'inline',
        p: {
          display: 'inline',
        },
        a: {
          color: 'vimeoBlue.700',
          _dark: { color: 'vimeoBlue.600' },
          textDecoration: 'underline',
          cursor: 'pointer',
          transition: 'color 0.2s ease',
          _hover: {
            color: 'vimeoBlue.800',
            _dark: { color: 'vimeoBlue.500' },
          },
        },
        '.highlighted-text': {
          backgroundColor: 'orange.100',
        },
        '.highlighted-mention-text': {
          backgroundColor: 'orange.100',
          ...(isResolved
            ? {
                color: 'gray.900',
                opacity: 1,
              }
            : {
                color: 'fill-brand',
              }),
        },
      },
    }),
    [isResolved],
  );

  if (!editor) {
    return null;
  }

  return (
    <Box display="inline" sx={defaultRichTextViewerStyles}>
      <EditorContent className="rich-comment-viewer" editor={editor} />
      <EditedText editedTime={editedTime} />
    </Box>
  );
};

interface EditedTextProps {
  editedTime?: string;
}

const EditedText: React.FC<EditedTextProps> = memo(({ editedTime }) =>
  editedTime ? (
    <Tooltip placement="top" label={convertDateTime(editedTime)}>
      <BokehText
        display="inline"
        color="text-secondary"
        variant="body-md"
      >{` (${T.Edited})`}</BokehText>
    </Tooltip>
  ) : null,
);
